export interface VehicleData {
  vin: string
  make: string
  model: string
  trim: string
  year: number
  exteriorColor: string
  interiorColor: string
  startingOdometerMiles: number
  additionalDetails: string
}

export interface ParseResult {
  success: boolean
  vehicles: VehicleData[]
  count: number
  error?: string
}

export function parseCSV(csvContent: string): ParseResult {
  try {
    const lines = csvContent.trim().split('\n')
    if (lines.length < 2) {
      return {
        success: false,
        vehicles: [],
        count: 0,
        error: 'CSV must have at least a header row and one data row'
      }
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const vehicles: VehicleData[] = []

    // Required headers mapping
    const headerMap = {
      vin: ['vin', 'vehicle_identification_number'],
      make: ['make', 'manufacturer'],
      model: ['model'],
      trim: ['trim', 'variant'],
      year: ['year', 'model_year'],
      exteriorColor: ['exterior_color', 'exteriorcolor', 'color', 'paint_color'],
      interiorColor: ['interior_color', 'interiorcolor', 'interior'],
      startingOdometerMiles: ['starting_odometer_miles', 'odometer', 'miles', 'mileage'],
      additionalDetails: ['additional_details', 'details', 'notes']
    }

    // Find column indices for each field
    const columnIndices: Record<string, number> = {}
    for (const [field, possibleHeaders] of Object.entries(headerMap)) {
      const index = headers.findIndex(h => possibleHeaders.includes(h))
      if (index !== -1) {
        columnIndices[field] = index
      }
    }

    // Validate required fields
    const requiredFields = ['vin', 'make', 'model', 'year']
    const missingFields = requiredFields.filter(field => columnIndices[field] === undefined)
    if (missingFields.length > 0) {
      return {
        success: false,
        vehicles: [],
        count: 0,
        error: `Missing required columns: ${missingFields.join(', ')}`
      }
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim())

      try {
        const vehicle: VehicleData = {
          vin: values[columnIndices.vin] || '',
          make: values[columnIndices.make] || '',
          model: values[columnIndices.model] || '',
          trim: values[columnIndices.trim] || '',
          year: parseInt(values[columnIndices.year]) || new Date().getFullYear(),
          exteriorColor: values[columnIndices.exteriorColor] || '',
          interiorColor: values[columnIndices.interiorColor] || '',
          startingOdometerMiles: parseInt(values[columnIndices.startingOdometerMiles]) || 0,
          additionalDetails: values[columnIndices.additionalDetails] || ''
        }

        // Basic validation
        if (!vehicle.vin || !vehicle.make || !vehicle.model) {
          continue // Skip invalid rows
        }

        vehicles.push(vehicle)
      } catch (error) {
        console.warn(`Skipping invalid row ${i + 1}:`, error)
      }
    }

    return {
      success: true,
      vehicles,
      count: vehicles.length,
      error: vehicles.length === 0 ? 'No valid vehicle data found' : undefined
    }
  } catch (error) {
    return {
      success: false,
      vehicles: [],
      count: 0,
      error: `CSV parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`
    }
  }
}

export function parseJSON(jsonContent: string): ParseResult {
  try {
    const data = JSON.parse(jsonContent)
    let vehicles: VehicleData[] = []

    // Handle both array of vehicles and single vehicle
    if (Array.isArray(data)) {
      vehicles = data
    } else if (typeof data === 'object' && data !== null) {
      vehicles = [data]
    } else {
      return {
        success: false,
        vehicles: [],
        count: 0,
        error: 'JSON must be an object or array of objects'
      }
    }

    // Validate and normalize each vehicle
    const validVehicles: VehicleData[] = []
    for (const vehicle of vehicles) {
      try {
        const normalizedVehicle: VehicleData = {
          vin: vehicle.vin || '',
          make: vehicle.make || '',
          model: vehicle.model || '',
          trim: vehicle.trim || '',
          year: typeof vehicle.year === 'number' ? vehicle.year : parseInt(vehicle.year) || new Date().getFullYear(),
          exteriorColor: vehicle.exteriorColor || '',
          interiorColor: vehicle.interiorColor || '',
          startingOdometerMiles: typeof vehicle.startingOdometerMiles === 'number' ? vehicle.startingOdometerMiles :
                                parseInt(vehicle.startingOdometerMiles) || 0,
          additionalDetails: vehicle.additionalDetails || ''
        }

        // Basic validation
        if (!normalizedVehicle.vin || !normalizedVehicle.make || !normalizedVehicle.model) {
          continue // Skip invalid vehicles
        }

        validVehicles.push(normalizedVehicle)
      } catch (error) {
        console.warn('Skipping invalid vehicle:', error)
      }
    }

    return {
      success: true,
      vehicles: validVehicles,
      count: validVehicles.length,
      error: validVehicles.length === 0 ? 'No valid vehicle data found' : undefined
    }
  } catch (error) {
    return {
      success: false,
      vehicles: [],
      count: 0,
      error: `JSON parsing error: ${error instanceof Error ? error.message : 'Invalid JSON format'}`
    }
  }
}

export function parseFile(file: File): Promise<ParseResult> {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const content = e.target?.result as string
      if (!content) {
        resolve({
          success: false,
          vehicles: [],
          count: 0,
          error: 'Failed to read file content'
        })
        return
      }

      const fileExtension = file.name.toLowerCase().split('.').pop()

      if (fileExtension === 'csv') {
        resolve(parseCSV(content))
      } else if (fileExtension === 'json') {
        resolve(parseJSON(content))
      } else {
        resolve({
          success: false,
          vehicles: [],
          count: 0,
          error: 'Unsupported file format. Please use CSV or JSON files.'
        })
      }
    }

    reader.onerror = () => {
      resolve({
        success: false,
        vehicles: [],
        count: 0,
        error: 'Failed to read file'
      })
    }

    reader.readAsText(file)
  })
}
